/**
 * Normalize Axios / API error payloads for user-visible messages.
 * Supports FastAPI HTTPException, shared ErrorResponse envelope,
 * validation arrays, and the Issue #567 ``ErrorEnvelope`` shape.
 */
import type { AxiosError } from 'axios';

/**
 * Closed registry of error codes emitted by the backend.
 * Must mirror ``shared/models/error_envelope.py::ErrorCode``.
 */
export type ErrorCode =
  | 'MISSING_IDENTIFIER_CONSTANTS'
  | 'CANNOT_LINK_HIERARCHY'
  | 'INCOMPLETE_LEVEL_IDENTITY'
  | 'ANCESTRY_COLUMN_NOT_MAPPABLE'
  | 'ASSEMBLY_ERROR'
  | 'TEMPLATE_PARSE_ERROR'
  | 'CORRECTION_VALIDATION_ERROR'
  | 'VALIDATION_ERROR'
  | 'INTERNAL_ERROR'
  | 'REMOTE_UPSTREAM_ERROR';

/**
 * Issue #567 structured error envelope.
 * Every structured backend exception serializes into this shape via
 * its ``.to_envelope()`` method; the UI consumes typed fields
 * rather than regexing free-text detail strings.
 */
export interface ErrorEnvelope {
  code: ErrorCode;
  message: string;
  payload: Record<string, unknown>;
  remediation?: string | null;
  request_id?: string | null;
}

// ── Per-code payload shapes (Issue #567) ─────────────────────────────
// Each variant mirrors the `payload` produced by a specific
// exception's ``to_envelope()`` on the backend. Consumers can
// narrow via ``isEnvelope(env, 'CODE')`` to access typed fields
// without casting.

export interface MissingIdentifierConstantsPayload {
  missing: Array<{ table: string; column: string }>;
  remedies?: string[];
}

export interface CannotLinkHierarchyPayload {
  child: string;
  ancestor: string;
  child_sources: string[];
  ancestor_sources: string[];
  missing_columns: string[];
  reason?: string | null;
}

export interface IncompleteLevelIdentityViolation {
  level_element: string;
  target_table?: string | null;
  missing_identity_columns: string[];
  other_mapped_fields?: string[];
}

export interface IncompleteLevelIdentityPayload {
  violations: IncompleteLevelIdentityViolation[];
}

export interface AssemblyErrorPayload {
  level?: string;
  missing_column?: string;
  available_columns?: string[];
  detail?: string;
}

export interface TemplateParseErrorPayload {
  format?: string;
  path?: string;
  line?: number;
  detail?: string;
}

export interface CorrectionValidationErrorPayload {
  errors: Array<Record<string, unknown>>;
}

export interface RemoteUpstreamErrorPayload {
  upstream?: ErrorEnvelope;
  status_code?: number;
}

export interface InternalErrorPayload {
  exception_type?: string;
  component?: string;
}

export interface AncestryColumnNotMappablePayload {
  column?: string;
  ancestor_element?: string;
  reason?: string;
}

export interface ValidationErrorPayload {
  errors?: Array<Record<string, unknown>>;
}

/**
 * Per-code payload map — lookup for narrowing ``envelope.payload`` to a
 * typed shape based on ``envelope.code``. Keeping it centralised here
 * means adding a new backend exception is a two-line change: add to the
 * ``ErrorCode`` Literal and the shape here.
 */
export interface ErrorPayloads {
  MISSING_IDENTIFIER_CONSTANTS: MissingIdentifierConstantsPayload;
  CANNOT_LINK_HIERARCHY: CannotLinkHierarchyPayload;
  INCOMPLETE_LEVEL_IDENTITY: IncompleteLevelIdentityPayload;
  ANCESTRY_COLUMN_NOT_MAPPABLE: AncestryColumnNotMappablePayload;
  ASSEMBLY_ERROR: AssemblyErrorPayload;
  TEMPLATE_PARSE_ERROR: TemplateParseErrorPayload;
  CORRECTION_VALIDATION_ERROR: CorrectionValidationErrorPayload;
  VALIDATION_ERROR: ValidationErrorPayload;
  INTERNAL_ERROR: InternalErrorPayload;
  REMOTE_UPSTREAM_ERROR: RemoteUpstreamErrorPayload;
}

/**
 * Type-narrowing typeguard: after ``isEnvelope(env, 'INCOMPLETE_LEVEL_IDENTITY')``
 * returns true, ``env.payload`` is typed as ``IncompleteLevelIdentityPayload``
 * and fields like ``env.payload.violations`` can be accessed directly.
 */
export function isEnvelope<C extends ErrorCode>(
  env: ErrorEnvelope | null | undefined,
  code: C
): env is ErrorEnvelope & { code: C; payload: ErrorPayloads[C] } {
  return !!env && env.code === code;
}

const KNOWN_ERROR_CODES: ReadonlySet<string> = new Set<ErrorCode>([
  'MISSING_IDENTIFIER_CONSTANTS',
  'CANNOT_LINK_HIERARCHY',
  'INCOMPLETE_LEVEL_IDENTITY',
  'ANCESTRY_COLUMN_NOT_MAPPABLE',
  'ASSEMBLY_ERROR',
  'TEMPLATE_PARSE_ERROR',
  'CORRECTION_VALIDATION_ERROR',
  'VALIDATION_ERROR',
  'INTERNAL_ERROR',
  'REMOTE_UPSTREAM_ERROR',
]);

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

/**
 * Extract a typed :class:`ErrorEnvelope` from an Axios / fetch error
 * when the backend returned one. Returns ``null`` for error shapes
 * the UI should continue handling via the legacy path (bare HTTP
 * errors, validation arrays, etc.).
 *
 * Canvas error renderers can switch on ``envelope.code`` to produce
 * per-exception UI (per-level remediation chips for
 * ``INCOMPLETE_LEVEL_IDENTITY``, actionable buttons for
 * ``MISSING_IDENTIFIER_CONSTANTS``, etc.).
 */
export function getApiErrorEnvelope(error: unknown): ErrorEnvelope | null {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return null;
  }
  const ax = error as AxiosError<unknown>;
  const raw = ax.response?.data;
  if (!isRecord(raw)) {
    return null;
  }
  const code = raw.code;
  const message = raw.message;
  if (
    typeof code === 'string' &&
    KNOWN_ERROR_CODES.has(code) &&
    typeof message === 'string' &&
    'payload' in raw
  ) {
    return {
      code: code as ErrorCode,
      message,
      payload: (isRecord(raw.payload) ? raw.payload : {}) as Record<
        string,
        unknown
      >,
      remediation:
        typeof raw.remediation === 'string' ? raw.remediation : null,
      request_id: typeof raw.request_id === 'string' ? raw.request_id : null,
    };
  }
  return null;
}

export function getApiErrorMessage(error: unknown): string {
  // Prefer the typed ``ErrorEnvelope`` when the backend returned one —
  // the envelope's ``message`` is already human-readable.
  const envelope = getApiErrorEnvelope(error);
  if (envelope) {
    return envelope.message;
  }
  // NOTE (Issue #567): The legacy ``detail`` / ``error`` / ``msg`` parsing
  // below remains deliberately. Not every backend route in the platform
  // has been migrated to raise through the global envelope handler yet:
  // mapping-service's ``routes/schemas.py``, ``routes/rule_templates.py``
  // and several prompt-orchestrator routers (``imports``, ``messages``,
  // ``operations``, ``phases``, ``pipelines``, ``profiles``, ``sessions``,
  // ``workspace``) still raise inline ``HTTPException(detail=...)``.
  // Removing this fallback would degrade error messages for those routes
  // to the literal string "Request failed" — visible regression with no
  // upside. The fallback is retired route-by-route as each router is
  // migrated to envelope-raising; the invariant for structured /
  // per-code UI (remediation chips, actionable retries) is
  // ``getApiErrorEnvelope()``, which does NOT touch this path.
  if (error && typeof error === 'object' && 'response' in error) {
    const ax = error as AxiosError<unknown>;
    const raw = ax.response?.data;
    if (typeof raw === 'string' && raw.trim()) {
      return raw;
    }
    if (isRecord(raw)) {
      const detail = raw.detail;
      if (typeof detail === 'string' && detail.trim()) {
        // Intercept common DuckDB catalog errors for better UI
        if (detail.includes('Catalog Error: Table with name') && detail.includes('does not exist')) {
          const match = detail.match(/Table with name "([^"]+)" does not exist|Table with name (\w+) does not exist/);
          const tableName = match ? (match[1] || match[2]) : 'requested';
          return `Source table "${tableName}" not found. It may have been deleted or the pipeline state is stale.`;
        }
        return detail;
      }
      if (Array.isArray(detail)) {
        const parts = detail.map((item) => {
          if (isRecord(item) && typeof item.msg === 'string') {
            return item.msg;
          }
          return JSON.stringify(item);
        });
        return parts.join('; ');
      }
      const errField = raw.error;
      if (typeof errField === 'string' && errField.trim()) {
        return errField;
      }
      const msg = raw.message;
      if (typeof msg === 'string' && msg.trim()) {
        return msg;
      }
    }
    if (typeof ax.message === 'string' && ax.message !== 'Request failed with status code 500') {
      return ax.message;
    }
    const status = ax.response?.status;
    if (status) {
      return `Request failed (${status})`;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Request failed';
}
